import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search, Radio } from "lucide-react";
import { Input } from "../components/ui/input";
import { getPartnerApplications } from "../lib/api";

const STATUSES = ["all", "Submitted", "In Review", "Approved", "Declined", "On Hold"];
const STATUS_COLOR = {
  Submitted: "text-brand-indigo", "In Review": "text-brand-pink", Approved: "text-green-400",
  Declined: "text-red-400", "On Hold": "text-zinc-400",
};

export default function Partners() {
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("all");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const t = setTimeout(() => {
      setLoading(true);
      getPartnerApplications({ status, q: q || undefined }).then(setRows).finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(t);
  }, [status, q]);

  return (
    <div className="p-6 md:p-10">
      <h1 className="font-display text-3xl font-black text-zinc-50">Creative Service Providers</h1>
      <p className="mt-2 text-sm text-zinc-500">Creative Service Provider applications — what they provide and how they price it.</p>

      <div className="mt-8 flex flex-col gap-3 md:flex-row md:items-center">
        <div className="relative md:max-w-sm md:flex-1">
          <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-600" />
          <Input data-testid="partner-search" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search name, email, city, service…"
            className="h-12 rounded-xl border-zinc-800 bg-zinc-900/70 pl-11 text-zinc-100 placeholder:text-zinc-600" />
        </div>
        <div className="flex flex-wrap gap-2">
          {STATUSES.map((s) => (
            <button key={s} data-testid={`partner-filter-${s}`} onClick={() => setStatus(s)}
              className={`font-mono-tech rounded-full border px-4 py-2 text-[10px] transition-colors duration-200 ${
                status === s ? "border-brand-indigo bg-brand-indigo text-white" : "border-zinc-800 text-zinc-400 hover:border-zinc-600 hover:text-zinc-100"}`}>
              {s}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-8 grid gap-4 sm:grid-cols-3">
        {[["Applications", rows.length],
          ["Approved", rows.filter((r) => r.status === "Approved").length],
          ["Awaiting review", rows.filter((r) => r.status === "Submitted" || r.status === "In Review").length]].map(([k, v]) => (
          <div key={k} className="border border-zinc-900 bg-zinc-900/40 p-6" data-testid={`partner-stat-${k}`}>
            <p className="font-mono-tech text-[10px] text-zinc-500">{k}</p>
            <p className="mt-2 font-display text-3xl font-black text-zinc-50">{v}</p>
          </div>
        ))}
      </div>

      <div className="mt-10 border-t border-zinc-900" data-testid="partners-table">
        {loading && <p className="font-mono-tech py-8 text-[11px] text-zinc-500">Loading…</p>}
        {!loading && rows.length === 0 && (
          <p className="py-10 text-sm text-zinc-500">
            No creative service provider applications yet. <Link to="/join/creative-service-provider" className="text-brand-indigo">Open the provider flow</Link>.
          </p>
        )}
        {rows.map((r) => (
          <Link key={r.id} to={`/control/providers/${r.id}`} data-testid={`partner-row-${r.id}`}
            className="grid grid-cols-1 gap-3 border-b border-zinc-900 px-1 py-5 transition-colors duration-200 hover:bg-zinc-900/50 md:grid-cols-[1.6fr_1.6fr_1fr_auto] md:items-center">
            <div>
              <p className="font-display font-bold text-zinc-50">{r.display_name}</p>
              <p className="font-mono-tech text-[10px] text-zinc-500">
                {[r.contact?.city, r.contact?.state_province].filter(Boolean).join(", ")} · {r.created_at?.slice(0, 10)}
              </p>
            </div>
            <p className="text-sm text-zinc-400">{(r.services?.selected || []).slice(0, 4).join(", ")}{(r.services?.selected || []).length > 4 ? "…" : ""}</p>
            <p className={`font-mono-tech text-[10px] ${STATUS_COLOR[r.status] || "text-zinc-400"}`}>{r.status}</p>
            <div className="flex flex-wrap gap-1.5">
              {(r.services?.categories || []).slice(0, 2).map((c) => (
                <span key={c} className="font-mono-tech rounded-sm border border-zinc-800 px-2 py-1 text-[9px] text-zinc-400">{c}</span>
              ))}
              {r.notion?.synced && (
                <span className="font-mono-tech rounded-sm border border-green-500/40 bg-green-500/10 px-2 py-1 text-[9px] text-green-400">
                  <Radio className="inline h-2.5 w-2.5" /> SYNCED
                </span>
              )}
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
